import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TagInput } from '@nucleus-commerce/admin-kit/forms';
import type { StaffProfileStoresItem } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Label } from '@nucleus-commerce/ui/components/label';

import { useBulkErrorMessage } from '@/shared/bulk/use-bulk-error-message';

import { StoreChecklist } from './store-checklist';

/**
 * The bulk actions that need input or confirmation before they run, with how many products they
 * cover (the ticked rows, or every matching product).
 */
export interface BulkInputAction {
  kind: 'add_tags' | 'remove_tags' | 'publish' | 'clear_schedule' | 'delete';
  count: number;
}

export type BulkInputResult =
  | { kind: 'add_tags' | 'remove_tags'; tags: string[] }
  | { kind: 'publish'; storeIds: string[] }
  | { kind: 'clear_schedule'; storeIds: string[] }
  | { kind: 'delete' };

const STORE_ACTIONS = { publish: 'publish', clear_schedule: 'clearSchedule' } as const;

export interface BulkInputDialogProps {
  action: BulkInputAction | null;
  stores: ReadonlyArray<Pick<StaffProfileStoresItem, 'id' | 'name'>>;
  pending: boolean;
  error: unknown;
  onSubmit: (result: BulkInputResult) => void;
  onClose: () => void;
}

function Body({
  action,
  stores,
  pending,
  error,
  onSubmit,
  onClose,
}: Omit<BulkInputDialogProps, 'action'> & { action: BulkInputAction }) {
  const { t } = useTranslation(['products', 'common']);
  const message = useBulkErrorMessage();
  const [tags, setTags] = useState<string[]>([]);
  const [storeIds, setStoreIds] = useState<string[]>([]);
  const { count } = action;
  const storeAction =
    action.kind === 'publish' || action.kind === 'clear_schedule'
      ? STORE_ACTIONS[action.kind]
      : null;
  const inStores = storeAction !== null;

  const title = storeAction
    ? t(`products:bulk.${storeAction}Title`, { count })
    : action.kind === 'delete'
      ? t('products:bulk.deleteTitle', { count })
      : t(`products:bulk.tagsTitle_${action.kind === 'add_tags' ? 'add' : 'remove'}`, { count });
  const ready = action.kind === 'delete' || (inStores ? storeIds.length > 0 : tags.length > 0);

  const submit = () => {
    if (action.kind === 'publish' || action.kind === 'clear_schedule') {
      onSubmit({ kind: action.kind, storeIds });
    } else if (action.kind === 'delete') onSubmit({ kind: 'delete' });
    else onSubmit({ kind: action.kind, tags });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        {storeAction ? (
          <DialogDescription>{t(`products:bulk.${storeAction}Description`)}</DialogDescription>
        ) : action.kind === 'delete' ? (
          <DialogDescription>{t('products:bulk.deleteDescription')}</DialogDescription>
        ) : null}
      </DialogHeader>
      {action.kind === 'add_tags' || action.kind === 'remove_tags' ? (
        <div className="grid gap-1.5">
          <Label htmlFor="bulk-tags">{t('products:bulk.tagsLabel')}</Label>
          <TagInput
            id="bulk-tags"
            value={tags}
            onChange={setTags}
            max={50}
            maxLength={60}
            placeholder={t('products:bulk.tagsPlaceholder')}
            removeLabel={(tag) => t('products:fields.removeTag', { tag })}
          />
        </div>
      ) : null}
      {inStores ? (
        <StoreChecklist
          legend={t('products:bulk.storesLabel')}
          stores={stores}
          value={storeIds}
          onChange={setStoreIds}
        />
      ) : null}
      {error ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription>{message(error)}</AlertDescription>
        </Alert>
      ) : null}
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          type="button"
          variant={action.kind === 'delete' ? 'destructive' : 'default'}
          disabled={!ready || pending}
          onClick={submit}
        >
          {pending
            ? t('products:bulk.working')
            : action.kind === 'delete'
              ? t('products:bulk.delete')
              : t('products:bulk.apply')}
        </Button>
      </DialogFooter>
    </>
  );
}

/**
 * Tags to add or remove, stores to publish into or clear scheduled dates in, or a delete
 * confirmation for the selection.
 */
export function BulkInputDialog(props: BulkInputDialogProps) {
  const { action, onClose } = props;
  return (
    <Dialog open={action !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        {action ? <Body key={action.kind} {...props} action={action} /> : null}
      </DialogContent>
    </Dialog>
  );
}
